import { db, generateId } from "./db.js";
import { ref } from "vue";
import { getUser } from "./user.js";
import { sendInvites } from "./backend-api.js";
import { getSubscription } from "./subscription.js";
import firebase from "firebase/app";

// ==================== CREATE ====================

async function createFirm(data, userId, transaction) {
  data.owner = userId;
  data.createdAt = firebase.firestore.FieldValue.serverTimestamp();
  // create a new doc ref so we have the id whether or not a transaction is used
  const firmRef = db.collection("firms").doc();
  if (transaction) {
    transaction.set(firmRef, data);
    return { id: firmRef.id, ...data };
  } else {
    await firmRef.set(data);
  }
  const firm = { id: firmRef.id, ...data };
  return firm;
}

// add user as admin member to firm and update user's firms subcollection and firm's members subcollection
// because of the firebase rules, this cannot be done together with createFirm in a transaction
async function addAdminUserToFirm(firmId, userId) {
  const membersRef = db.doc(`firms/${firmId}/members/${userId}`);
  const userFirmRef = db.doc(`users/${userId}/firms/${firmId}`);

  await db.runTransaction(async (transaction) => {
    const firmRef = db.doc(`firms/${firmId}`);
    const firmSnap = await transaction.get(firmRef);
    if (!firmSnap.exists) {
      throw new Error("Firm does not exist");
    }

    const user = await getUser(userId);
    const firmData = firmSnap.data();

    transaction.set(membersRef, {
      uid: userId,
      role: "admin",
      status: "joined",
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      phoneNumber: user.phoneNumber,
    });

    transaction.set(userFirmRef, {
      firmId,
      name: firmData.name,
      role: "admin",
    });
  });
}

// ==================== READ ====================

async function getFirmsByUser(userId) {
  try {
    const snapshot = await db.collection(`users/${userId}/firms`).get();
    return snapshot.docs.map((doc) => ({ ...doc.data() }));
  } catch (error) {
    console.log("Error fetching firms: " + error);
    return [];
  }
}

async function getOrphanedFirmByUser(userId) {
  try {
    const snapshot = await db.collection("firms").where("owner", "==", userId).get();
    if (snapshot.empty) return null;
    const doc = snapshot.docs[0];
    return { id: doc.id, ...doc.data() };
  } catch (error) {
    console.log("Error fetching firms: " + error);
    return null;
  }
}

async function getFirm(firmId) {
  return db.doc(`firms/${firmId}`)
    .get()
    .then(
      (firm) => {
        return firm.exists ? { id: firm.id, ...firm.data() } : null;
      });
}

async function getFirmSettings(firmId) {
  const firm = await getFirm(firmId);
  if (firm) {
    return firm.settings ?? {};
  }
  else {
    return null;
  }
}

async function getFirmLLMs(firmId) {
  const firm_settings = await getFirmSettings(firmId);
  if (firm_settings && firm_settings.llms && firm_settings.llms.length > 0) {
    return firm_settings.llms;
  }
  else {
    return null;
  }
}

async function getExtraApplications(firmId) {
  const firm_settings = await getFirmSettings(firmId);
  if (firm_settings && firm_settings.extra_applications) {
    return firm_settings.extra_applications;
  }
  else {
    return "";
  }
}

async function getDocketReminders(firmId) {
  const firm_settings = await getFirmSettings(firmId);
  if (firm_settings && firm_settings.docket_reminders) {
    return firm_settings.docket_reminders;
  }
  else {
    return [];
  }
}

async function getInitialImportationSetback(firmId) {
  const firm_settings = await getFirmSettings(firmId);
  if (firm_settings && firm_settings.initialImportationSetback) {
    return firm_settings.initialImportationSetback;
  }
  else {
    return 9; // default setback
  }
}

async function getDefaultFirm(userId) {
  const user = await db.doc(`users/${userId}`).get();
  return user.exists ? user.data().defaultFirm : null;
}

async function isUserInFirm(userId, firmId) {
  const membership = await db.doc(`firms/${firmId}/members/${userId}`).get().catch(() => {
    console.log("could not read firm");
    return false;
  });
  // data() is undefined when the membership doc does not exist
  return !!(membership && membership.exists && membership.data()["status"] == "joined");
}

// ==================== UPDATE ====================

function updateFirm(firmId, data) {
  return db.doc(`firms/${firmId}`).update(data);
}

function inviteMember(firmId, role, email = "") {
  const id = generateId();
  return db.doc(`firms/${firmId}/invites/${id}`).set({
    id,
    role,
    status: "invited",
    email,
  }).then(() => sendInvites(firmId));
}

// client portal invite: same invites collection, but role "client" + clientId so
// the backend accept flow links the new member to the CRM client record (portal
// access to shared cases/documents is scoped by that clientId). NOTE: clientId is
// the CORPORATION (CRM client); the invitee is an individual contact there (e.g.
// in-house counsel) — firstName/lastName are the contact's. Language is NOT set —
// the portal UI switches languages live.
// Dedup: one active invite/membership per email per firm — inviting the same
// contact twice must not create a second seat/invite.
async function inviteClient(firmId, clientId, email = "", contact = {}) {
  const existing = await db
    .collection(`firms/${firmId}/invites`)
    .where("email", "==", email)
    .where("status", "in", ["invited", "sent"])
    .get();
  if (!existing.empty) {
    const err = new Error("An invitation for this email is already pending.");
    err.code = "duplicate-invite";
    throw err;
  }
  const members = await db
    .collection(`firms/${firmId}/members`)
    .where("email", "==", email)
    .where("status", "==", "joined")
    .get();
  if (!members.empty) {
    const err = new Error("This email already has portal access.");
    err.code = "duplicate-member";
    throw err;
  }

  const id = generateId();
  return db.doc(`firms/${firmId}/invites/${id}`).set({
    id,
    role: "client",
    clientId,
    status: "invited",
    email,
    ...(contact.firstName ? { firstName: contact.firstName } : {}),
    ...(contact.lastName ? { lastName: contact.lastName } : {}),
  }).then(() => sendInvites(firmId));
}

function getInvites(firmId) {
  const invites = ref([]);
  db.collection(`firms/${firmId}/invites`).get().then((snapshot) => {
    invites.value = snapshot.docs.map((doc) => ({ ...doc.data() }));
  });
  return invites;
}

// ==================== NO DELETE ALLOWED ====================

// firm docketing rules related methods

async function updateDocketingRule(firmId, docketingRule) {
  const settings = await getFirmSettings(firmId);
  if (settings == null) {
    // this means the firm does not exist
    return null;
  }
  else {
    settings.docketingRules = { ...settings.docketingRules, ...docketingRule }
    return updateFirm(firmId, {settings: settings});
  }
}

async function removeDocketingRule(firmId, code) {
  const settings = await getFirmSettings(firmId);
  if (settings == null) {
    // this means the firm does not exist
    return null;
  }
  else {
    if (settings.docketingRules) {
      delete settings.docketingRules[code];
      return updateFirm(firmId, {settings: settings});
    }
    return null;
  }
}

async function getDocketingRule(firmId, code) {
  const settings = await getFirmSettings(firmId);
  if (settings == null) {
    // this means the firm does not exist
    return null;
  }
  else {
    if (settings.docketingRules) {
      return settings.docketingRules[code];
    }
    return null;
  }
}

async function getDocketingRules(firmId) {
  const settings = await getFirmSettings(firmId);
  if (settings == null) {
    return null;
  }
  else {
    return settings.docketingRules ?? {};
  }
}

async function getIgnoreList(firmId) {
  const settings = await getFirmSettings(firmId);
  if (settings == null) {
    return null;
  }
  else {
    return settings.ignoreList ?? [];
  }
}

async function addCodeToIgnoreList(firmId, documentCode) {
  const settings = await getFirmSettings(firmId);
  if (settings == null) {
    // the firm does not exist
    return null;
  }
  else {
    if (settings.ignoreList) {
      if (!settings.ignoreList.includes(documentCode)) {
        settings.ignoreList.push(documentCode);
      }
    }
    else {
      settings.ignoreList = [documentCode];
    }
    return updateFirm(firmId, {settings: settings});
  }
}

async function removeCodeFromIgnoreList(firmId, documentCode) {
  const settings = await getFirmSettings(firmId);
  if (settings == null) {
    // the firm does not exist
    return null;
  }
  else {
    if (settings.ignoreList) {
      const index = settings.ignoreList.indexOf(documentCode);
      if (index !== -1) {
        settings.ignoreList.splice(index, 1);
      }
    }
    return updateFirm(firmId, {settings: settings});
  }
}

async function updateIgnoreList(firmId, newlist) {
  const settings = await getFirmSettings(firmId);
  if (settings == null) {
    // the firm does not exist
    return null;
  }
  else {
    settings.ignoreList = newlist;
    return updateFirm(firmId, {settings: settings});
  }
}

async function getSubscriptionData(firmId) {
  const firm = await getFirm(firmId);
  if (firm) {
    return await getSubscription(firm.subscriptionId);
  }
  return null;
}

async function updateDocketReminders(firmId, docketReminders) {
  const settings = await getFirmSettings(firmId);
  if (settings == null) {
    return null;
  }
  else {
    settings.docket_reminders = docketReminders;
    return updateFirm(firmId, {settings: settings});
  }
}

async function updateExtraApplications(firmId, extraApplications) {
  const settings = await getFirmSettings(firmId);
  if (settings == null) {
    return null;
  }
  else {
    settings.extra_applications = extraApplications;
    return updateFirm(firmId, {settings: settings});
  }
}

async function UpdateInitialImportationSetback(firmId, setback) {
  // make sure setback is an integer between 3 and 9, inclusive
  if (!Number.isInteger(setback) || setback < 3 || setback > 9) {
    throw new Error("Setback must be an integer between 3 and 9");
  }
  else {
    const settings = await getFirmSettings(firmId);
    if (settings == null) {
      return null;
    }
    else {
      settings.initialImportationSetback = setback;
      return updateFirm(firmId, {settings: settings});
    }
  }
}

// firm-configurable option (firms/{firmId}.settings.maintenanceTaskOption) for
// how far ahead maintenance fee tasks are docketed:
//   'default'  - only phases whose paying window has already opened
//   'all'      - all upcoming unpaid phases, even if the window is not open yet
//   'upcoming' - only the single next unpaid phase, whether its window is open or not
const MAINTENANCE_TASK_OPTION_DEFAULT = 'default';
const MAINTENANCE_TASK_OPTION_ALL = 'all';
const MAINTENANCE_TASK_OPTION_UPCOMING = 'upcoming';
// ordered narrow -> broad: index doubles as rank for the "broadening creates
// additional tasks for already-imported patents" warnings in both settings UIs
const MAINTENANCE_TASK_OPTIONS = [
  MAINTENANCE_TASK_OPTION_DEFAULT,
  MAINTENANCE_TASK_OPTION_UPCOMING,
  MAINTENANCE_TASK_OPTION_ALL,
];

async function getMaintenanceTaskOption(firmId) {
  const settings = await getFirmSettings(firmId);
  if (settings == null) {
    return null;
  }
  return settings.maintenanceTaskOption ?? MAINTENANCE_TASK_OPTION_DEFAULT;
}

async function updateMaintenanceTaskOption(firmId, option) {
  if (!MAINTENANCE_TASK_OPTIONS.includes(option)) {
    throw new Error(`maintenanceTaskOption must be one of: ${MAINTENANCE_TASK_OPTIONS.join(", ")}`);
  }
  const settings = await getFirmSettings(firmId);
  if (settings == null) {
    return null;
  }
  settings.maintenanceTaskOption = option;
  return updateFirm(firmId, {settings: settings});
}

export {
  createFirm,
  getFirmsByUser,
  inviteMember,
  inviteClient,
  getInvites,
  getDefaultFirm,
  getFirm,
  isUserInFirm,
  updateFirm,
  updateDocketingRule,
  getFirmSettings,
  getDocketingRules,
  getIgnoreList,
  addCodeToIgnoreList,
  removeCodeFromIgnoreList,
  removeDocketingRule,
  updateIgnoreList,
  getDocketingRule,
  getSubscriptionData,
  getFirmLLMs,
  addAdminUserToFirm,
  getOrphanedFirmByUser,
  getDocketReminders,
  updateDocketReminders,
  getExtraApplications,
  updateExtraApplications,
  getInitialImportationSetback,
  UpdateInitialImportationSetback,
  getMaintenanceTaskOption,
  updateMaintenanceTaskOption,
  MAINTENANCE_TASK_OPTIONS,
};
